import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { ArrowUpFromLine, ArrowDownToLine, Clock } from "lucide-react";

// Local day key (YYYY-MM-DD) — kept self-contained so this panel has no
// dependency on ActionList's internal helpers.
function dayKey(date) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function StatChip({ icon: Icon, label, value, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Box component="span" sx={{ display: "inline-flex", color, flexShrink: 0 }}>
        <Icon size={15} strokeWidth={2.5} />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.tertiary", fontSize: "0.62rem", lineHeight: 1.1 }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

export function TodayStatsPanel({ actions = [] }) {
  const stats = useMemo(() => {
    const todayKey = dayKey(new Date());
    const now = Date.now();
    const ids = new Set();
    let pickups = 0, delivers = 0, remaining = 0;

    for (const a of actions) {
      if (!a.time || dayKey(a.time) !== todayKey) continue;
      ids.add(a.deliveryId);
      if (a.kind === "pickup") pickups++; else delivers++;
      if (a.time.getTime() >= now) remaining++;
    }
    return { deliveries: ids.size, pickups, delivers, remaining };
  }, [actions]);

  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2.5 },
        px: 1.75, py: 1.25, mb: 1.5,
        border: "0.5px solid", borderColor: "divider",
        borderRadius: 2, bgcolor: "background.paper",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary", textTransform: "uppercase",
            letterSpacing: 0.5, fontWeight: 700, display: "block",
          }}
        >
          Tänään
        </Typography>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}
        >
          {stats.deliveries} {stats.deliveries === 1 ? "kuljetus" : "kuljetusta"}
        </Typography>
      </Box>

      <StatChip icon={ArrowUpFromLine} label="Noudot"      value={stats.pickups}   color="info.700" />
      <StatChip icon={ArrowDownToLine} label="Toimitukset" value={stats.delivers}  color="success.700" />
      <StatChip icon={Clock}           label="Jäljellä"    value={stats.remaining} color="warning.800" />
    </Box>
  );
}